import { useState } from "react";
import { Paper, Stack, Typography } from "@mui/material";
import aiChatMessageStyles from "./aiChatMessageStyles";
import { AiChatMessage as AiChatMessageType } from "../../types/AIChat/AiChatMessage";
import PendingExecutionJobCard from "./ExecutionJob/PendingExecutionJobCard";
import CompletedExecutionJob from "./ExecutionJob/CompletedExecutionJob";
import remarkGfm from "remark-gfm";
import ReactMarkdown from "react-markdown";
import AiChatMessageActions from "./AiChatMessageActions";
import AiChatMessageEditor from "./AiChatMessageEditor";
import { useAiChat } from "../../provider/AiChatProvider";
import { enqueueSnackbar } from "notistack";

type AiChatMessageProps = {
  message: AiChatMessageType;
  variants?: AiChatMessageType[];
  onSelectVariant?: (message: AiChatMessageType) => Promise<void>;
  isInteractionDisabled?: boolean;
};

export default function AiChatMessage({
  message,
  variants = [],
  onSelectVariant,
  isInteractionDisabled = false,
}: AiChatMessageProps) {
  const { editMessage, isLoading } = useAiChat();
  const [copyActionVisible, setCopyActionVisible] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const interactionsDisabled = isInteractionDisabled || isLoading;

  const saveEdit = async (content: string) => {
    try {
      await editMessage(message, content);
      setIsEditing(false);
    } catch {
      enqueueSnackbar("Die bearbeitete Nachricht konnte nicht gesendet werden.", { variant: "error" });
    }
  };

  return (
    <Stack
      sx={{
        ...aiChatMessageStyles.messageContainer,
        ...(message.role === "user"
          ? aiChatMessageStyles.humanMessageContainer
          : aiChatMessageStyles.assistantMessageContainer),
      }}
      onClick={() => setCopyActionVisible(true)}
    >
      {message.role === "user" ? (
        <Paper
          variant={"outlined"}
          sx={{
            ...aiChatMessageStyles.message,
            ...(isEditing ? aiChatMessageStyles.editor : aiChatMessageStyles.humanMessage),
          }}
        >
          {isEditing ? (
            <AiChatMessageEditor
              initialContent={message.content}
              disabled={interactionsDisabled}
              onCancel={() => setIsEditing(false)}
              onSave={saveEdit}
            />
          ) : (
            <Typography sx={{ whiteSpace: "pre-wrap" }}>{message.content}</Typography>
          )}
        </Paper>
      ) : (
        <>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>
          {message.executionJob?.status === "pendingApproval" && (
            <PendingExecutionJobCard executionJob={message.executionJob} />
          )}
          {message.executionJob && ["completed", "failed", "partiallyFailed"].includes(message.executionJob?.status) && (
            <CompletedExecutionJob executionJob={message.executionJob} />
          )}
        </>
      )}
      {!isEditing && (
        <AiChatMessageActions
          message={message}
          variants={variants}
          onSelectVariant={onSelectVariant}
          onStartEdit={() => setIsEditing(true)}
          isInteractionDisabled={interactionsDisabled}
          isCopyActionPinned={copyActionVisible}
        />
      )}
    </Stack>
  );
}
